import {
    petcoBrightGray,
    petcoMikadoYellow,
    petcoOnyx,
    petcoRustyRed,
    petcoWarning,
    petcoWhite,
} from "@common/_assets/color-constants.js";
import classNames from "classnames";
import React, { useMemo } from "react";
import { Control, Controller, FieldPath, FieldPathValue, FieldValues, NestedValue } from "react-hook-form";
import Select, { GroupBase, OnChangeValue, Props } from "react-select";
import { FormGroup } from "./FormGroup";
interface IFormSelectProps<
	TFieldValues extends FieldValues,
	TName extends FieldPath<TFieldValues>,
	Option,
	IsMulti extends boolean,
	Group extends GroupBase<Option>
> extends Omit<
		Props<Option, IsMulti, Group>,
		| "id"
		| "value"
		| "onChange"
		| "aria-label"
		| "aria-describedby"
		| "aria-errormessage"
		| "aria-invalid"
		| "styles"
	> {
	/**
	 * The name of the form field to which this dropdown should bind.
	 * The form value will be of this type.
	 */
	fieldName: TName;
	/**
	 * The react-hook-form control instance
	 */
	control: Control<TFieldValues, object>;
	/**
	 * The form field label
	 */
	label: string;
	/**
	 * If you need to render an element to the ride (on the tail) of the controls label, you can specify it here.
	 */
	labelTailElement?: JSX.Element;
	/**
	 * Given the current form value, returns the corresponding option to show as currently selected.
	 */
	mapValueToOption: (
		currentFormValue: NestedValue<FieldPathValue<TFieldValues, TName>>
	) => (IsMulti extends true ? Option[] : Option) | undefined;
	/**
	 * Given the currently selected option, returns the value to be set on the form.
	 */
	mapOptionToValue: (
		selectedOption: OnChangeValue<Option, IsMulti>
	) => FieldPathValue<TFieldValues, TName> | undefined | null;
	/**
	 * Optional: Fired when the selected option changes. Use this to implement custom form behaviors.
	 */
	onOptionChanged?: (selectedOption: OnChangeValue<Option, IsMulti>) => void;
	/**
	 * Optional help text, which will be shown small and dimmed under the form element.
	 */
	helpText?: string;
	/**
	 * (Optional; default: False) Indicates whether a modified value should be visually highlighted.
	 * This is preferred when editing an existing record but not when adding a new one.
	 */
	highlightDirty?: boolean;
	/**
	 * (Optional; default: True) Indicates whether a selection is required. This will be true in most cases.
	 */
	isRequired?: boolean;
	/**
	 * (Optional; default False) Used if the modal height is short and needs position relative.
	 * If false the dropdown shows in front of input elements instead of pushing input elements down and growing the modal.
	 * Send as a condition if a value exisiting causes other elements to load on the modal and meet the minimum height requirements.
	 * example usage: dropdownShouldGrowModal={ !attachmentType ? true : false }
	 */
	dropdownShouldGrowModal?: boolean;
	/**
	 * OPTIONAL: RHF rules.validate
	 * You can pass a callback function as the argument to validate, or you can pass an object of callback functions to validate all of them.
	 * This function will be executed on its own without depending on other validation rules included in the required attribute.
	 */
	validateRules?: any;
}

/** These styles accomplish two things:
 *   1. Set the appropriate border/background colors for dirty and invalid
 *   2. Make the control height conform to other form input elements but allow it to grow in a multiselect scenario.
 */
const selectStyles = (
	invalid: boolean,
	isDirty: boolean,
	highlightDirty: boolean,
	dropdownShouldGrowModal: boolean
) => ({
	control: (base: any, { isDisabled }: { isDisabled: boolean }) => ({
		...base,
		cursor: isDisabled ? "not-allowed" : "auto",
		border: invalid
			? "1px solid " + petcoRustyRed
			: isDirty && highlightDirty
			? "1px solid " + petcoMikadoYellow
			: "1px solid " + petcoOnyx,
		backgroundColor:
			!invalid && isDirty && highlightDirty ? petcoWarning : isDisabled ? petcoBrightGray : petcoWhite,
		fontFamily: "petcocirculartt-regular, Roboto;",
		fontSize: "14px",
		minHeight: 33,
	}),
	dropdownIndicator: (base: any) => ({
		...base,
		padding: 4,
	}),
	clearIndicator: (base: any) => ({
		...base,
		padding: 4,
	}),
	valueContainer: (base: any) => ({
		...base,
		padding: "0px 6px",
	}),
	multiValueLabel: (base: any) => ({
		...base,
		padding: "4px 6px",
		lineHeight: "16px",
	}),
	menu: (base: any) => ({
		...base,
		position: dropdownShouldGrowModal ? "relative" : "absolute",
	}),
	menuList: (base: any) => ({
		...base,
		fontFamily: "petcocirculartt-regular, Roboto;",
		fontSize: "14px",
	}),
	input: (base: any) => ({
		...base,
		margin: 0,
		padding: 0,
		fontFamily: "petcocirculartt-regular, Roboto;",
		fontSize: "14px",
	}),
});

/**
 * A form dropdown element based on react-select
 */
export function FormSelect<
	TFieldValues extends FieldValues,
	TName extends FieldPath<TFieldValues>,
	Option,
	IsMulti extends boolean = false,
	Group extends GroupBase<Option> = GroupBase<Option>
>({
	fieldName,
	control,
	label,
	mapValueToOption,
	mapOptionToValue,
	onOptionChanged,
	className,
	helpText,
	highlightDirty = false,
	isRequired = true,
	labelTailElement,
	dropdownShouldGrowModal = false,
	validateRules,
	...attrsFromProps
}: IFormSelectProps<TFieldValues, TName, Option, IsMulti, Group>) {
	const controlId = useMemo(() => `form-${label.replaceAll(/[\W_]/gi, "").toLowerCase()}`, [label]);
	const helpId = useMemo(() => `${controlId}-help`, [controlId]);
	const errorId = useMemo(() => `${controlId}-error`, [controlId]);

	const rules = useMemo(() => {
		let newRules = undefined;
		if (validateRules !== undefined)
			newRules = {
				min: {
					value: 1,
					message: "Required" },
				validate: validateRules
			};
		// !IMPORTANT: required overrides ValidateRules, so they should be mutually exlusive
		if (isRequired && validateRules === undefined)
			newRules = {
				required: "Required",
				min: {
					value: 1,
					message: "Required" }
			};
		return newRules;
	}, [isRequired, validateRules]);

	return (
		<Controller
			name={fieldName}
			control={control}
			rules={rules}
			render={({
				field: { value: formValue, onChange: changeFormValue, ref: fieldRef },
				fieldState: { invalid, error, isDirty },
			}) => (
				<FormGroup
					controlId={controlId}
					controlLabel={label}
					controlLabelTailElement={labelTailElement}
					helpId={helpId}
					helpText={helpText}
					errorId={errorId}
					errorMessage={error?.message}
				>
					<Select
						value={mapValueToOption(formValue) ?? null}
						onChange={selectedOption => {
							changeFormValue(mapOptionToValue(selectedOption) ?? null);
							onOptionChanged && onOptionChanged(selectedOption);
						}}
						id={controlId}
						aria-label={label}
						aria-describedby={helpText && helpId}
						aria-errormessage={error && errorId}
						aria-invalid={invalid}
						className={classNames(
							"petco-form-select",
							className,
							invalid && "is-invalid",
							isDirty && highlightDirty && "is-dirty"
						)}
						styles={selectStyles(invalid, isDirty, highlightDirty, dropdownShouldGrowModal)}
						{...attrsFromProps}
						ref={fieldRef}
					/>
				</FormGroup>
			)}
		/>
	);
}

export type TriStateSelectOption = {
	name: string;
	value: boolean | null;
};

export const TriStateSelectOptions: TriStateSelectOption[] = [
	{
		name: "Any",
		value: null,
	},
	{
		name: "Yes",
		value: true,
	},
	{
		name: "No",
		value: false,
	},
];
